// ContactsTable.tsx
'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Table, TableColumnDef } from '@/components/portal/Table';
import { EntityView } from '@/components/entities/EntityView';
import { ContactDTO, LeasingContactFilters } from '#types/types';
import { Briefcase, Building2, Mail, MapPin, Phone, User } from '@/components/ui/lucide-memo';

interface ContactsTableProps {
  /**
   * When provided, the table runs in "scoped" mode, used from the brand card to
   * list only the contacts attached to a specific brand, with Discover/Reset/
   * Remove affordances in the toolbar.
   *
   * When omitted, the table runs as the top-level Contacts inventory page
   * with a full-width browseable list of every contact in the workspace.
   */
  brandId?: string;
  scopedIds?: string[];
  lockedFilters?: Partial<LeasingContactFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  preview?: boolean;
  /** Drop the toolbar row (the brand card's Contacts tab, owner 2026-09-26). */
  hideToolbar?: boolean;
}

const EMPTY_SORT_OPTIONS: never[] = [];

const translations = {
  en: {
    title: 'All contacts',
    breadcrumb: 'Inventory / Contacts',
    name: 'Name',
    role: 'Role',
    company: 'Company',
    email: 'Email',
    phone: 'Phone',
    location: 'Location',
    emptyTitle: 'No decision makers on file for this brand yet.',
    reveal: 'Reveal',
    revealing: 'Revealing...',
  },
  fr: {
    title: 'Tous les contacts',
    breadcrumb: 'Inventaire / Contacts',
    name: 'Nom complet',
    role: 'Poste',
    company: 'Entreprise',
    email: 'Email',
    phone: 'Telephone',
    emptyTitle: 'Aucun decideur enregistre pour cette marque.',
    reveal: 'Reveler',
    revealing: 'Revelation...',
    location: 'Localisation',
  },
};

export const ContactsTable = React.memo(({
  brandId,
  scopedIds,
  lockedFilters,
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  preview = false,
  hideToolbar = false,
}: ContactsTableProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const isFaterAdmin = useKernelStore((state) => state.userContext.isFaterAdmin);
  const t = translations[lang];
  // The progress bar below already existed; it just could not see the AGENT. Discovery run from
  // chat left this table completely static, so the user was told "discovering contacts..." in
  // prose while the panel sat there looking broken. Reuse the same animation for both callers
  // rather than inventing a second one: local state for the button, kernel state for the agent.
  const [isDiscoveringLocal, setIsDiscovering] = useState(false);
  const agentDiscoveringThisBrand = useKernelStore(
    (state) => Boolean(brandId) && state.sourceContactDiscoveryBrandId === brandId,
  );
  const isDiscovering = isDiscoveringLocal || agentDiscoveringThisBrand;
  const [isResetting, setIsResetting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [discoverProgress, setDiscoverProgress] = useState(0);

  const isScoped = typeof brandId === 'string' && brandId.length > 0;

  // Progressive discovery bar: ramps toward ~92% while running, then snaps to 100% and
  // fades out once the results have landed.
  useEffect(() => {
    if (isDiscovering) {
      setDiscoverProgress((p) => (p < 8 ? 8 : p));
      const iid = window.setInterval(() => {
        setDiscoverProgress((p) => (p >= 92 ? p : Math.min(92, p + Math.max(0.5, (92 - p) * 0.07))));
      }, 350);
      return () => window.clearInterval(iid);
    }
    setDiscoverProgress((p) => (p > 0 ? 100 : 0));
    const tid = window.setTimeout(() => setDiscoverProgress(0), 600);
    return () => window.clearTimeout(tid);
  }, [isDiscovering]);

  const handleDiscover = useCallback(async () => {
    if (!kernel.current || !brandId) return;
    setIsDiscovering(true);
    try {
      const { summary } = await kernel.current.discoverBrandContacts(brandId, {
        maxResults: 12,
        enrich: true,
        revealPersonalEmails: true,
        revealPhoneNumber: true,
        seniorities: ['director', 'head', 'vp', 'c_suite', 'founder', 'owner', 'partner'],
        countries: ['France'],
        debug: (process.env.NEXT_PUBLIC_CONTACT_DISCOVERY_DEBUG || '0') === '1',
      });

      const found = summary.created + summary.updated;
      if (found === 0) {
        toast.info('No contacts found.');
      } else {
        toast.success(`${found} contact${found > 1 ? 's' : ''} found.`);
      }
    } catch (error: any) {
      const message = error?.message || 'Contact discovery failed';
      toast.error(message);
      console.error(`[ContactsDiscovery][${brandId}] Failed`, error);
    } finally {
      setIsDiscovering(false);
    }
  }, [kernel, brandId]);

  const handleReset = useCallback(async () => {
    if (!kernel.current || !brandId) return;
    setIsResetting(true);
    try {
      await kernel.current.clearBrandContacts(brandId);
      await kernel.current.syncEntity('Brand', brandId);
    } finally {
      setIsResetting(false);
    }
  }, [kernel, brandId]);

  const handleRemove = useCallback(async (contactId: string) => {
    if (!kernel.current || !brandId) return;
    setRemovingId(contactId);
    try {
      await kernel.current.removeBrandContact(brandId, contactId);
      await kernel.current.syncEntity('Brand', brandId);
    } catch (e) {
      console.error(e);
    } finally {
      setRemovingId(null);
    }
  }, [kernel, brandId]);

  const handleRemoveEntity = useCallback((entity: { id: string }) => {
    void handleRemove(entity.id);
  }, [handleRemove]);

  const isActionLocked = isDiscovering || isResetting || removingId !== null;

  const columns = useMemo<TableColumnDef[]>(() => {
    if (isScoped) {
      // Compact column set for brand-scoped usage (no company column, they
      // all belong to the same brand).
      return [
        { id: 'avatar', minWidth: 40, header: '' },
        { id: 'name', icon: User, minWidth: 150, header: t.name },
        { id: 'role', icon: Briefcase, minWidth: 150, header: t.role },
        { id: 'email', icon: Mail, minWidth: 160, header: t.email },
        { id: 'phone', icon: Phone, minWidth: 140, header: t.phone },
        { id: 'actions', minWidth: 100, header: '', align: 'right' },
      ];
    }
    // Full column set for the top-level inventory page.
    return [
      { id: 'avatar', minWidth: 40, header: '' },
      { id: 'name', icon: User, minWidth: 180, header: t.name },
      { id: 'role', icon: Briefcase, minWidth: 160, header: t.role },
      { id: 'company', icon: Building2, minWidth: 180, header: t.company },
      { id: 'email', icon: Mail, minWidth: 200, header: t.email },
      { id: 'phone', icon: Phone, minWidth: 140, header: t.phone },
      { id: 'location', icon: MapPin, minWidth: 160, header: t.location },
    ];
  }, [isScoped, t]);

  const resolvedLockedFilters: Partial<LeasingContactFilters> | undefined = useMemo(() => {
    if (isScoped) {
      return { parentId: brandId, parentType: 'brand', ...(lockedFilters || {}) };
    }
    return lockedFilters;
  }, [isScoped, brandId, lockedFilters]);

  const renderToolbarExtras = useCallback(() => (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-[11px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded mr-2">
        {scopedIds?.length ?? 0} contacts
      </span>
      <button
        type="button"
        onClick={handleDiscover}
        disabled={isActionLocked}
        className={clsx(
          'h-7 rounded-md border px-3 text-[11px] font-normal transition-colors',
          isActionLocked
            ? 'cursor-not-allowed border-white/10 bg-white/[0.03] text-white/25'
            : 'border-white/15 bg-white/[0.06] text-white/70 hover:bg-white/10 hover:text-white'
        )}
      >
        {isDiscovering ? 'Discovering...' : 'Discover'}
      </button>
      {isFaterAdmin && (
        <button
          type="button"
          onClick={handleReset}
          disabled={isActionLocked || (scopedIds?.length ?? 0) === 0}
          className={clsx(
            'h-7 rounded-md border px-3 text-[11px] font-normal transition-colors',
            (isActionLocked || (scopedIds?.length ?? 0) === 0)
              ? 'cursor-not-allowed border-white/10 bg-white/[0.03] text-white/25'
              : 'border-white/15 bg-white/[0.06] text-white/70 hover:bg-white/10 hover:text-white'
          )}
        >
          {isResetting ? 'Resetting...' : 'Reset'}
        </button>
      )}
    </div>
  ), [scopedIds, handleDiscover, handleReset, isActionLocked, isDiscovering, isResetting, isFaterAdmin]);

  const renderRow = useCallback((
    contact: ContactDTO,
    _isExpanded: boolean,
    toggle: () => void,
    currentColumns?: TableColumnDef[],
  ) => (
    <EntityView
      type="Contact"
      entityId={contact.id}
      variant="row"
      onClick={toggle}
      columns={currentColumns}
      onDelete={isScoped && isFaterAdmin ? handleRemoveEntity : undefined}
    />
  ), [isScoped, isFaterAdmin, handleRemoveEntity]);

  return (
    <>
      {isScoped && (isDiscovering || discoverProgress > 0) && (
        <div className="mb-2">
          <div className="mb-1 text-[11px] text-[#B5B5B5]">{lang === 'fr' ? 'Recherche de contacts…' : 'Finding contacts…'}</div>
          <div className="h-[6px] w-full overflow-hidden rounded-full bg-[#2A2A2A]">
            <div className="h-full rounded-full transition-[width] duration-300 ease-out" style={{ width: `${discoverProgress}%`, background: 'linear-gradient(90deg, #0C2A82, #1E50C8, #8AAAEE)' }} />
          </div>
        </div>
      )}
    <Table
      entityType="Contact"
      preview={preview}
      scopedIds={isScoped ? scopedIds : undefined}
      lockedFilters={resolvedLockedFilters}
      columns={columns}
      assetLedgerTitle={assetLedgerTitle ?? (isScoped ? undefined : t.title)}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb ?? (isScoped ? undefined : t.breadcrumb)}
      sortOptions={EMPTY_SORT_OPTIONS}
      renderToolbarExtras={isScoped ? renderToolbarExtras : undefined}
      hideToolbar={hideToolbar}
      // "No results found." IS THE WRONG WORD FOR A LIST NOBODY HAS FILLED YET. Contacts do not
      // exist until someone asks for them, so an empty table is not a failed search, it is an
      // invitation - and the toolbar's Discover button is easy to miss at the top of an empty page.
      // The action is offered where the eye already is. Owner's instruction, 2026-08-16.
      emptyStateContent={
        isScoped ? (
          <div className="flex flex-col items-center gap-2.5 p-12 text-center">
            <p className="text-[14px] text-[#8E8E8E]">{t.emptyTitle}</p>
            <button
              type="button"
              onClick={handleDiscover}
              disabled={isActionLocked || isDiscovering}
              className="h-9 rounded-full border border-[#262626] bg-[#1B1B1B] px-4 text-[#E5E5E5] transition-colors hover:bg-[#2A2A2A] hover:text-white disabled:opacity-50"
              style={{ fontSize: 13 }}
            >
              {isDiscovering ? t.revealing : t.reveal}
            </button>
          </div>
        ) : undefined
      }
      renderRow={renderRow}
    />
    </>
  );
});

ContactsTable.displayName = 'ContactsTable';
